<template>
  <div class="tms-log-page">
    <div class="toolbar">
      <div class="title-block">
        <h1>Журнал TMS</h1>
        <span class="counter">{{ totalItems }} записей</span>
      </div>
      <button class="btn" type="button" @click="fetchData">Обновить</button>
    </div>

    <div class="filters">
      <label class="field">
        <span>Рейс</span>
        <input
          type="text"
          class="input"
          v-model="form.trip_id"
          placeholder="№ рейса"
          @keyup.enter="applyFilters"
        />
      </label>
      <label class="field">
        <span>ID рейса</span>
        <input
          type="number"
          class="input"
          v-model="form.cargo_trip_id"
          placeholder="cargo_trip_id"
          @keyup.enter="applyFilters"
        />
      </label>
      <label class="field">
        <span>Поток</span>
        <select class="select" v-model="form.flow">
          <option v-for="opt in FLOW_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </label>
      <label class="field">
        <span>Действие</span>
        <select class="select" v-model="form.kind">
          <option v-for="opt in KIND_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </label>
      <label class="field">
        <span>Статус</span>
        <select class="select" v-model="form.status">
          <option v-for="opt in STATUS_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </label>
      <label class="field">
        <span>Ошибка</span>
        <select class="select" v-model="form.error_kind">
          <option v-for="opt in ERROR_KIND_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </label>
      <label class="field">
        <span>Источник</span>
        <select class="select" v-model="form.source_service">
          <option v-for="opt in SOURCE_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
      </label>
      <label class="field">
        <span>С даты</span>
        <input type="date" class="input" v-model="form.date_from" />
      </label>
      <label class="field">
        <span>По дату</span>
        <input type="date" class="input" v-model="form.date_to" />
      </label>
      <div class="filter-actions">
        <button class="btn primary" type="button" @click="applyFilters">Применить</button>
        <button class="btn" type="button" @click="resetFilters">Сбросить</button>
      </div>
    </div>

    <my-loader v-if="loading && items.length === 0 && !hasError" />

    <div v-else-if="hasError" class="state-block error">
      Не удалось загрузить журнал TMS-действий.
    </div>

    <div v-else class="table-wrap">
      <table v-if="items.length" class="log-table">
        <thead>
          <tr>
            <th>Дата</th>
            <th>Рейс</th>
            <th>Поток</th>
            <th>Действие</th>
            <th>Статус</th>
            <th>Ошибка</th>
            <th>Ресурс</th>
            <th>Попытка</th>
            <th>Источник</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template v-for="item in items" :key="item.id">
            <tr
              class="row"
              :class="{ row_open: isExpanded(item) }"
              @click="toggleExpand(item)"
            >
              <td>{{ formatDate(item.created_dt) }}</td>
              <td>
                <div class="trip">
                  <router-link
                    v-if="canOpenTrip(item.cargo_trip_id)"
                    class="trip-link"
                    :to="`/trips/${item.cargo_trip_id}`"
                    @click.stop
                  >{{ tripDisplay(item) || EMPTY_VALUE }}</router-link>
                  <span v-else>{{ tripDisplay(item) || EMPTY_VALUE }}</span>
                  <span class="trip-id" v-if="item.cargo_trip_id">#{{ item.cargo_trip_id }}</span>
                </div>
              </td>
              <td>
                <div>{{ flowLabel(item.flow) }}</div>
                <div class="muted">{{ directionLabel(item.direction) }}</div>
              </td>
              <td>{{ kindLabel(item.kind) }}</td>
              <td><span class="badge" :class="statusClass(item.status)">{{ item.status || EMPTY_VALUE }}</span></td>
              <td>
                <span v-if="item.error_kind" class="error-kind">{{ item.error_kind }}</span>
                <span v-else>{{ EMPTY_VALUE }}</span>
              </td>
              <td class="resource">
                <div v-if="item.driver_name">🚛 {{ item.driver_name }}</div>
                <div v-if="item.car_number">🚗 {{ item.car_number }}</div>
                <div v-if="item.trailer_number">🛻 {{ item.trailer_number }}</div>
                <span v-if="!item.driver_name && !item.car_number && !item.trailer_number">{{ EMPTY_VALUE }}</span>
              </td>
              <td>{{ item.attempt_no || EMPTY_VALUE }}</td>
              <td>{{ item.source_service || EMPTY_VALUE }}</td>
              <td class="expand-cell">
                <span class="chevron" :class="{ open: isExpanded(item) }">▸</span>
              </td>
            </tr>
            <tr v-if="isExpanded(item)" class="detail-row">
              <td colspan="10">
                <div class="detail">
                  <div class="detail-grid">
                    <div class="d-row"><span class="lbl">ID записи</span><span class="val">{{ item.id }}</span></div>
                    <div class="d-row"><span class="lbl">Создано</span><span class="val">{{ item.created_dt || EMPTY_VALUE }}</span></div>
                    <div class="d-row"><span class="lbl">Поток</span><span class="val">{{ flowLabel(item.flow) }} · {{ directionLabel(item.direction) }}</span></div>
                    <div class="d-row"><span class="lbl">Действие</span><span class="val">{{ kindLabel(item.kind) }}</span></div>
                    <div class="d-row"><span class="lbl">Статус</span><span class="val">{{ item.status || EMPTY_VALUE }}</span></div>
                    <div class="d-row"><span class="lbl">Ошибка</span><span class="val">{{ item.error_kind || EMPTY_VALUE }}</span></div>
                    <div class="d-row"><span class="lbl">Попытка</span><span class="val">{{ item.attempt_no || EMPTY_VALUE }}</span></div>
                    <div class="d-row"><span class="lbl">Актор</span><span class="val">{{ item.actor || EMPTY_VALUE }}</span></div>
                    <div class="d-row"><span class="lbl">Источник</span><span class="val">{{ item.source_service || EMPTY_VALUE }}</span></div>
                    <div class="d-row"><span class="lbl">cargo_trip_id</span><span class="val">{{ item.cargo_trip_id || EMPTY_VALUE }}</span></div>
                    <div class="d-row" v-if="item.tms_id"><span class="lbl">tms_id</span><span class="val">{{ item.tms_id }}</span></div>
                    <div class="d-row" v-if="item.driver_name || item.driver_tms_id"><span class="lbl">Водитель</span><span class="val">{{ item.driver_name || EMPTY_VALUE }} <span v-if="item.driver_tms_id" class="muted">({{ item.driver_tms_id }})</span></span></div>
                    <div class="d-row" v-if="item.car_number || item.car_tms_id"><span class="lbl">Машина</span><span class="val">{{ item.car_number || EMPTY_VALUE }} <span v-if="item.car_tms_id" class="muted">({{ item.car_tms_id }})</span></span></div>
                    <div class="d-row" v-if="item.trailer_number || item.trailer_tms_id"><span class="lbl">Прицеп</span><span class="val">{{ item.trailer_number || EMPTY_VALUE }} <span v-if="item.trailer_tms_id" class="muted">({{ item.trailer_tms_id }})</span></span></div>
                  </div>
                  <div v-if="item.error_detail" class="detail-block">
                    <div class="detail-title">Детали ошибки</div>
                    <pre class="detail-pre">{{ item.error_detail }}</pre>
                  </div>
                  <div v-if="item.tms_message" class="detail-block">
                    <div class="detail-title">Ответ TMS</div>
                    <pre class="detail-pre">{{ item.tms_message }}</pre>
                  </div>
                  <div v-if="item.cargo_trip_id" class="detail-history">
                    <button class="btn history-btn" type="button" @click="toggleHistory(item)">
                      {{ isHistoryOpen(item) ? '▾ Скрыть историю рейса' : '▸ История рейса' }}
                    </button>
                    <timeline
                      v-if="isHistoryOpen(item)"
                      :cargo-trip-id="item.cargo_trip_id"
                      :highlight-id="item.id"
                    />
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <div v-else class="state-block">
        Записей по выбранным фильтрам нет.
      </div>
    </div>

    <my-pagination
      v-if="totalPages > 1"
      :currentPage="currentPage"
      :totalPages="totalPages"
      @pageChanged="pageChanged"
    />
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import Timeline from './TmsActionTimeline.vue';
import { formatDate, EMPTY_VALUE } from '@/pages/pureBidding/helpers';

const FLOW_OPTIONS = [
  { value: '', label: 'Все потоки' },
  { value: 'real', label: 'real' },
  { value: 'fake', label: 'fake' },
  { value: 'inbound_parse', label: 'inbound_parse' },
  { value: 'inbound_relink', label: 'inbound_relink' },
  { value: 'inbound_cancel', label: 'inbound_cancel' },
  { value: 'email', label: 'email' },
  { value: 'system', label: 'system' },
];

const KIND_OPTIONS = [
  { value: '', label: 'Все действия' },
  { value: 'candidate_send', label: 'candidate_send' },
  { value: 'attorney_send', label: 'attorney_send' },
  { value: 'candidate_withdraw', label: 'candidate_withdraw' },
  { value: 'status_sync', label: 'status_sync' },
  { value: 'cancel', label: 'cancel' },
  { value: 'relink', label: 'relink' },
  { value: 'registration', label: 'registration' },
  { value: 'stale_mark', label: 'stale_mark' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'success', label: 'success' },
  { value: 'error', label: 'error' },
  { value: 'pending', label: 'pending' },
  { value: 'info', label: 'info' },
];

const ERROR_KIND_OPTIONS = [
  { value: '', label: 'Все ошибки' },
  { value: 'index_error', label: 'index_error' },
  { value: 'none_tms_id', label: 'none_tms_id' },
  { value: 'api_reject', label: 'api_reject' },
  { value: 'http', label: 'http' },
  { value: 'validation', label: 'validation' },
  { value: 'network', label: 'network' },
  { value: 'duplicate', label: 'duplicate' },
  { value: 'null', label: 'null' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'Все источники' },
  { value: 'tms-service', label: 'tms-service' },
  { value: 'erp-backend', label: 'erp-backend' },
  { value: 'status-jobs', label: 'status-jobs' },
  { value: 'email-service', label: 'email-service' },
];

function labelFrom(options, value) {
  if (!value) return value;
  const found = options.find((opt) => opt.value === value);
  return found ? found.label : value;
}

export default {
  name: 'TmsActionLogPage',
  components: { Timeline },
  setup() {
    const store = useStore();
    const route = useRoute();
    const hasError = ref(false);
    const isFetching = ref(false);
    let pollTimer = null;
    const expanded = ref(new Set());
    const historyOpen = ref(new Set());
    const state = computed(() => store.state.tmsActionLog);

    const form = reactive({ ...state.value.filters });

    const fetchData = () => {
      if (isFetching.value) return;
      isFetching.value = true;
      store.dispatch('tmsActionLog/getLog')
        .then(() => { hasError.value = false; })
        .catch(() => { hasError.value = true; })
        .finally(() => { isFetching.value = false; });
    };

    const applyFilters = () => {
      store.dispatch('tmsActionLog/setFilters', { ...form });
    };

    const resetFilters = () => {
      Object.assign(form, {
        cargo_trip_id: '',
        trip_id: '',
        direction: '',
        flow: '',
        kind: '',
        status: '',
        error_kind: '',
        source_service: '',
        date_from: '',
        date_to: '',
      });
      store.dispatch('tmsActionLog/resetFilters');
    };

    const pageChanged = (page) => {
      store.dispatch('tmsActionLog/setPage', page);
    };

    const isExpanded = (item) => expanded.value.has(item.id);
    const toggleExpand = (item) => {
      const next = new Set(expanded.value);
      if (next.has(item.id)) {
        next.delete(item.id);
        const hist = new Set(historyOpen.value);
        hist.delete(item.id);
        historyOpen.value = hist;
      } else {
        next.add(item.id);
      }
      expanded.value = next;
    };

    const isHistoryOpen = (item) => Boolean(item.cargo_trip_id) && historyOpen.value.has(item.id);
    const toggleHistory = (item) => {
      if (!item.cargo_trip_id) return;
      const next = new Set(historyOpen.value);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
        store.dispatch('tmsActionLog/loadTimeline', { cargoTripId: item.cargo_trip_id, force: true });
      }
      historyOpen.value = next;
    };

    const tripDisplay = (item) => item.cargo_trip_trip_id || item.trip_id;
    const canOpenTrip = (tripId) => /^\d+$/.test(String(tripId || ''));
    const statusClass = (status) => `badge-${status || 'info'}`;

    onMounted(() => {
      const queryTrip = route?.query?.trip_id;
      if (queryTrip && !form.trip_id) {
        form.trip_id = String(queryTrip);
        applyFilters();
      } else {
        fetchData();
      }
      pollTimer = setInterval(fetchData, 30000);
    });
    onBeforeUnmount(() => {
      if (pollTimer) clearInterval(pollTimer);
    });

    return {
      EMPTY_VALUE,
      FLOW_OPTIONS,
      KIND_OPTIONS,
      STATUS_OPTIONS,
      ERROR_KIND_OPTIONS,
      SOURCE_OPTIONS,
      form,
      hasError,
      fetchData,
      applyFilters,
      resetFilters,
      pageChanged,
      isExpanded,
      toggleExpand,
      isHistoryOpen,
      toggleHistory,
      tripDisplay,
      canOpenTrip,
      statusClass,
      formatDate,
      flowLabel: (v) => labelFrom(FLOW_OPTIONS, v) || v || EMPTY_VALUE,
      kindLabel: (v) => labelFrom(KIND_OPTIONS, v) || v || EMPTY_VALUE,
      directionLabel: (v) => (v === 'inbound' ? '← inbound' : v === 'outbound' ? '→ outbound' : (v || '')),
      items: computed(() => state.value.items || []),
      loading: computed(() => Boolean(state.value.loading)),
      currentPage: computed(() => state.value.currentPage || 1),
      totalPages: computed(() => state.value.totalPages || 1),
      totalItems: computed(() => state.value.totalItems || 0),
    };
  },
};
</script>

<style scoped>
.tms-log-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 14px 16px;
  color: #333;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.title-block {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

h1 { margin: 0; font-size: 22px; line-height: 1.2; }
.counter { color: #667085; font-size: 13px; }

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 12px;
  padding: 10px;
  background: #f7f8fa;
  border: 1px solid #edf0f3;
  border-radius: 6px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 12px;
  color: #4b5563;
}

.input,
.select {
  height: 30px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 13px;
  padding: 0 6px;
  min-width: 130px;
}

.input { min-width: 120px; }

.filter-actions {
  display: flex;
  gap: 6px;
  align-items: flex-end;
}

.btn {
  height: 30px;
  padding: 0 12px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover { border-color: #007bff; color: #007bff; }
.btn.primary { background: #007bff; color: #fff; border-color: #007bff; }
.btn.primary:hover { background: #0069d9; color: #fff; }

.table-wrap { flex: 1; overflow: auto; }

.log-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.log-table th,
.log-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #edf0f3;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.log-table th {
  background: #f7f8fa;
  color: #444;
  font-weight: 700;
}

.row { cursor: pointer; }
.row:hover { background: #f3f6fb; }
.row_open { background: #eef4ff; }

.trip { display: flex; flex-direction: column; }
.trip-link { color: #0b63ce; font-weight: 600; text-decoration: none; }
.trip-link:hover { text-decoration: underline; }
.trip-id { color: #98a2b3; font-size: 11px; }

.muted { color: #98a2b3; font-size: 11px; }

.badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}
.badge-success { background: #e6f4ea; color: #1e7e34; }
.badge-error   { background: #fdeaea; color: #c0392b; }
.badge-pending { background: #fff5e0; color: #b7791f; }
.badge-info    { background: #e8eefc; color: #2563eb; }

.error-kind { color: #c0392b; font-weight: 600; }

.resource { white-space: normal !important; line-height: 1.35; }

.expand-cell { width: 24px; text-align: center; }
.chevron { display: inline-block; transition: transform .15s; color: #98a2b3; }
.chevron.open { transform: rotate(90deg); }

.detail-row td { background: #fbfcfe; padding: 0; }

.detail { padding: 10px 14px; }
.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 4px 20px;
  font-size: 12px;
}
.d-row { display: flex; gap: 6px; }
.d-row .lbl { color: #98a2b3; min-width: 104px; }
.d-row .val { color: #333; word-break: break-word; white-space: normal; }

.detail-block { margin-top: 8px; }
.detail-title { font-size: 11px; font-weight: 700; color: #667085; text-transform: uppercase; margin-bottom: 4px; }
.detail-pre {
  margin: 0;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #e6eaf0;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 260px;
  overflow: auto;
}

.detail-history { margin-top: 10px; }
.history-btn { font-size: 12px; }

.state-block { padding: 32px; color: #667085; font-size: 14px; }
.state-block.error { color: #dc3545; }
</style>
